import { useEffect, useState } from 'react'
import { useAuthServiceClient } from './sdk-client'
import { summarizeEntitlementSignal, type EntitlementSignal } from './entitlement-signal'

export type EntitlementsSignalState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; data: EntitlementSignal }

// Fetches just enough of `GET /api/v1/accounts/:slug/entitlements` to derive
// a lightweight per-account signal for the duplicate-review drawer -- never
// exposes the raw carrierEntitlements/networkEntitlements fields, which
// merge in wildcard rows and are not valid per-account evidence (see
// `summarizeEntitlementSignal`).
export function useAccountEntitlements(slug: string | null): EntitlementsSignalState {
  const getClient = useAuthServiceClient()
  const [state, setState] = useState<EntitlementsSignalState>(slug ? { status: 'loading' } : { status: 'idle' })

  useEffect(() => {
    if (!slug) {
      setState({ status: 'idle' })
      return
    }

    let cancelled = false
    setState({ status: 'loading' })

    getClient()
      .then((client) => client.auth.getAccountEntitlements(slug))
      .then((response) => {
        if (!cancelled) {
          setState({ status: 'ready', data: summarizeEntitlementSignal(response.data) })
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Failed to load entitlements.',
          })
        }
      })

    return () => {
      cancelled = true
    }
  }, [getClient, slug])

  return state
}
